import { Link } from '@tanstack/react-router';
import type { LucideIcon } from 'lucide-react';

import type { ObrasLink } from '../../features/obras/search.js';
import { cn } from '../../lib/cn.js';

export interface BarRow {
  readonly key: string;
  readonly label: string;
  readonly sublabel?: string | undefined;
  /** Com link, o rótulo leva à lista de obras já recortada no que a linha conta. */
  readonly link?: ObrasLink | undefined;
  /**
   * Ícone à esquerda do rótulo, na cor do estado que a linha representa.
   *
   * É o único lugar da lista com cor: a barra é neutra. O estado vai no
   * ícone e no texto, nunca na marca — cor como único portador falha em
   * campo e em preto e branco (design.md §4).
   */
  readonly icon?: LucideIcon | undefined;
  readonly iconTone?: string | undefined;
  readonly value: number;
  /** Fração da barra em tom escuro: o pago dentro do contratado, em [0, 1]. */
  readonly fill?: number | undefined;
  /** O valor por extenso, à direita. */
  readonly display: string;
  readonly detail?: string | undefined;
  /** Uma pendência da linha, em `danger`, abaixo do valor. */
  readonly warning?: string | undefined;
}

/**
 * Barras horizontais de uma série só, cada linha com o valor escrito.
 *
 * Com `fill`, a barra inteira fica no cinza claro e a parte paga no escuro —
 * a mesma linguagem do cartão de obras, em que o claro é a referência e o
 * escuro é o dinheiro que entrou. Sem `fill`, a barra é o cinza escuro de
 * sempre.
 */
export function BarList({
  rows,
  max,
  labelWidth = 'md',
}: {
  rows: readonly BarRow[];
  max?: number | undefined;
  /** `sm` para lista dentro de coluna estreita: o rótulo cede espaço à barra. */
  labelWidth?: 'sm' | 'md' | undefined;
}) {
  const teto = max ?? rows.reduce((maior, row) => Math.max(maior, row.value), 0);

  return (
    <ul className="flex flex-col gap-2.5">
      {rows.map((row) => {
        const largura = teto > 0 ? (row.value / teto) * 100 : 0;
        const comPreenchimento = row.fill !== undefined;

        return (
          <li
            key={row.key}
            className={cn(
              'grid items-center gap-3',
              labelWidth === 'sm'
                ? 'grid-cols-[minmax(0,7.5rem)_1fr_auto]'
                : 'grid-cols-[minmax(0,7.5rem)_1fr_auto] sm:grid-cols-[minmax(0,10rem)_1fr_auto]',
            )}
          >
            <span className="flex min-w-0 items-center gap-2 text-[13px]">
              {row.icon ? (
                <row.icon
                  size={14}
                  strokeWidth={1.75}
                  className={cn('shrink-0', row.iconTone ?? 'text-muted-foreground')}
                  aria-hidden
                />
              ) : null}
              <span className="min-w-0">
                {row.link ? (
                  <Link
                    to={row.link.to}
                    search={row.link.search ?? {}}
                    title="Ver na lista de obras"
                    className="block truncate transition-colors hover:text-primary-strong"
                  >
                    {row.label}
                  </Link>
                ) : (
                  <span className="block truncate">{row.label}</span>
                )}
                {row.sublabel ? (
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {row.sublabel}
                  </span>
                ) : null}
              </span>
            </span>

            <span
              className="relative h-2.5 w-full"
              role="img"
              aria-label={`${row.label}: ${row.display}${row.detail ? `, ${row.detail}` : ''}`}
            >
              <span
                className={cn(
                  'absolute inset-y-0 left-0 rounded-r-[4px]',
                  comPreenchimento ? 'bg-foreground/25' : 'bg-foreground/75',
                )}
                style={{ width: `${Math.max(largura, row.value > 0 ? 1 : 0)}%` }}
              />
              {comPreenchimento ? (
                <span
                  className="absolute inset-y-0 left-0 rounded-r-[4px] bg-foreground"
                  style={{ width: `${Math.max(largura * (row.fill ?? 0), 0)}%` }}
                />
              ) : null}
            </span>

            <span className="tabular text-right text-[13px] leading-tight">
              <span className="block font-medium">{row.display}</span>
              {row.warning ? (
                <span className="block text-[11px] font-medium text-danger">{row.warning}</span>
              ) : null}
              {row.detail ? (
                <span className="block text-[11px] text-muted-foreground">{row.detail}</span>
              ) : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
